import classNames from "classnames";
import React, { FunctionComponent, useCallback, useMemo } from "react";
import { useLocalizedText } from "../../hooks/context-provider-hooks/use-localized-text";
import { Button, IconButton } from "../button";
import {
  BDS_PAGINATION_ITEMS_DEFAULT_TEXT,
  BDS_PAGINATION_ITEMS_PER_PAGE_DEFAULT_TEXT,
  BDS_PAGINATION_NEXT_PAGE_DEFAULT_TEXT,
  BDS_PAGINATION_OF_ITEMS_DEFAULT_TEXT,
  BDS_PAGINATION_PAGE_DEFAULT_TEXT,
  BDS_PAGINATION_PREVIOUS_PAGE_DEFAULT_TEXT,
  BDS_PAGINATION_TOTAL_ITEMS_PLACEHOLDER,
  BDS_PAGINATION_VIEW_ALL_ITEMS_DEFAULT_TEXT,
  BDS_TEXT_TEMPLATE_KEYS,
} from "../constants";
import { IconNavArrowLeft, IconNavArrowRight } from "../icons";
import { Select, SelectOption } from "../select";
import { PaginationButtons } from "./pagination-buttons";
import { CSS_CLASSES } from "./pagination-css-classes";
import { PaginationColorOption } from "./pagination.types";

export interface PaginationProps {
  /**
   * This value represents how many items are visible on the page.
   *
   * It controls the option that appears selected in the itemsPerPage Select element.
   */
  itemsPerPage: number;

  /**
   * List of options for the itemsPerPage Select element.
   *
   * An additional item for the `All` option is addded to this list. It's value is set to 0.
   *
   * So the list of options passed to this prop SHOULD NOT include any items with value 0.
   */
  itemsPerPageOptions?: SelectOption[];

  /**
   * Event emitted when itemsPerPage selection is changed.
   *
   * Any attached event handler is called with the selected option object.
   */
  onItemsPerPageChange: (value: SelectOption) => void;

  /**
   * This value represents total items across all pages.
   */
  totalItems: number;

  /**
   * Index of the currently active page.
   */
  page: number;

  /**
   * Event emitted when the selected page is changed by clicking a specific page, next or previous buttons.
   *
   * Any attached event handler is called with the new selected page index.
   */
  onPageChange: (page: number) => void;

  /**
   * Event emitted on `view all items` button click.
   *
   * This button is only visible on mobile view.
   */
  onViewAllClick?: () => void;

  /**
   * This prop control is the View All button is rendered in the mobile view or not.
   *
   * @default false
   */
  hideViewAll?: boolean;

  /**
   * This prop controls hiding the Items per page dropdown.
   *
   * @default false
   */
  hidePageinationDropdown?: boolean;

  /**
   * Apply/override styling by passing in custom CSS class-name(s) using this prop.
   */
  className?: string;

  /**
   * ID of the container element for the whole Pagination component
   */
  id?: string;

  /**
   * This prop controls the color of the itemsPerPage dropdown and pagination buttons.
   *
   * @default "interactive"
   */
  color?: PaginationColorOption;
}

const defaultItemsPerPageOptions: SelectOption[] = [
  { value: "10", label: "10" },
  { value: "20", label: "20" },
  { value: "50", label: "50" },
  { value: "100", label: "100" },
  { value: "0", label: "All" },
];

/**
 * The `Pagination` component allows controlling lists pagination.
 *
 * UX for mobile version of this component is different - decrease screen size to see differences.
 *
 * Read on to understand the UX intent behind each of the prop values.
 */
export const Pagination: FunctionComponent<PaginationProps> = ({
  itemsPerPage,
  itemsPerPageOptions = defaultItemsPerPageOptions,
  onItemsPerPageChange,
  totalItems,
  page,
  onPageChange,
  onViewAllClick,
  hideViewAll = false,
  hidePageinationDropdown = false,
  className,
  id,
  color = "interactive",
  ...restProps
}) => {
  const localizedText = useLocalizedText(true);

  const rootClassName = useMemo(() => classNames(className, CSS_CLASSES.PAGINATION_ROOT), [className]);

  const lastPage = useMemo(() => Math.ceil(totalItems / itemsPerPage), [itemsPerPage, totalItems]);
  const startItemIndex = useMemo(() => (page - 1) * itemsPerPage + 1, [page, itemsPerPage]);
  const endItemIndex = useMemo(
    () =>
      startItemIndex + (itemsPerPage - 1) > totalItems || itemsPerPage === 0
        ? totalItems
        : startItemIndex + (itemsPerPage - 1),
    [itemsPerPage, startItemIndex, totalItems]
  );

  const getText = useCallback(
    (localizedKey: string, defaultLabel: string) => {
      return localizedText ? localizedText(localizedKey, undefined, false, defaultLabel) : defaultLabel;
    },
    [localizedText]
  );

  const ofItemsDefaultLabelText = BDS_PAGINATION_OF_ITEMS_DEFAULT_TEXT.replace(
    `{${BDS_PAGINATION_TOTAL_ITEMS_PLACEHOLDER}}`,
    totalItems.toString()
  );
  const ofItemsLabelText = localizedText
    ? localizedText(
        BDS_TEXT_TEMPLATE_KEYS.BDS_PAGINATION_OF_ITEMS,
        {
          [BDS_PAGINATION_TOTAL_ITEMS_PLACEHOLDER]: totalItems.toString(),
        },
        false,
        ofItemsDefaultLabelText
      )
    : ofItemsDefaultLabelText;

  return (
    <div id={id} className={rootClassName} {...restProps}>
      <div className={CSS_CLASSES.PAGINATION_OPTIONS}>
        {!hidePageinationDropdown && (
          <>
            <span className={CSS_CLASSES.PAGINATION_OPTIONS_TEXT}>
              {getText(
                BDS_TEXT_TEMPLATE_KEYS.BDS_PAGINATION_ITEMS_PER_PAGE,
                BDS_PAGINATION_ITEMS_PER_PAGE_DEFAULT_TEXT
              )}
              :
            </span>
            <Select
              className={CSS_CLASSES.PAGINATION_OPTIONS_SELECT}
              color={color}
              options={itemsPerPageOptions}
              name="itemsPerPage"
              selected={itemsPerPageOptions.find(({ value }) => value === itemsPerPage.toString())}
              onSelect={onItemsPerPageChange}
              id={id ? `${id}-itemsPerPage` : undefined}
              label={getText(
                BDS_TEXT_TEMPLATE_KEYS.BDS_PAGINATION_ITEMS_PER_PAGE,
                BDS_PAGINATION_ITEMS_PER_PAGE_DEFAULT_TEXT
              )}
            />

            <span className={CSS_CLASSES.PAGINATION_OPTIONS_TEXT}>
              {startItemIndex} - {endItemIndex}
              {ofItemsLabelText}
            </span>
          </>
        )}
      </div>
      <div className={CSS_CLASSES.PAGINATION_PAGE_BUTTONS_ROOT}>
        {itemsPerPage !== 0 && (
          <PaginationButtons color={color} page={page} onPageChange={onPageChange} lastPage={lastPage} />
        )}
      </div>

      <div className={CSS_CLASSES.PAGINATION_ROOT_MOBILE}>
        <IconButton
          className={classNames({ [CSS_CLASSES.PAGINATION_ARROW_BUTTON_HIDDEN]: page === 1 })}
          onClick={() => onPageChange(page - 1)}
          label={getText(
            BDS_TEXT_TEMPLATE_KEYS.BDS_PAGINATION_PREVIOUS_PAGE,
            BDS_PAGINATION_PREVIOUS_PAGE_DEFAULT_TEXT
          )}
          variant="ghost"
          size="large"
          color={color}
          icon={<IconNavArrowLeft />}
        />
        <div className={CSS_CLASSES.PAGINATION_MOBILE_INFO}>
          <div>
            <div className={CSS_CLASSES.PAGINATION_OPTIONS_TEXT}>
              <span className={CSS_CLASSES.PAGINATION_OPTIONS_TEXT_MARKED}>
                {getText(BDS_TEXT_TEMPLATE_KEYS.BDS_PAGINATION_ITEMS, BDS_PAGINATION_ITEMS_DEFAULT_TEXT)}{" "}
                {startItemIndex} - {endItemIndex}
              </span>
              {ofItemsLabelText}
            </div>
            <div className={CSS_CLASSES.PAGINATION_OPTIONS_TEXT}>
              <span className={CSS_CLASSES.PAGINATION_OPTIONS_TEXT_MARKED}>
                {getText(BDS_TEXT_TEMPLATE_KEYS.BDS_PAGINATION_PAGE, BDS_PAGINATION_PAGE_DEFAULT_TEXT)} {page}
              </span>{" "}
              of {lastPage}
            </div>
          </div>
          {!hideViewAll && (
            <Button
              label={getText(
                BDS_TEXT_TEMPLATE_KEYS.BDS_PAGINATION_VIEW_ALL_ITEMS,
                BDS_PAGINATION_VIEW_ALL_ITEMS_DEFAULT_TEXT
              )}
              variant="ghost"
              size="small"
              color={color}
              onClick={onViewAllClick}
              className={CSS_CLASSES.PAGINATION_ALL_ITEMS_LINK}
            />
          )}
        </div>
        <IconButton
          className={classNames({ [CSS_CLASSES.PAGINATION_ARROW_BUTTON_HIDDEN]: page === lastPage })}
          onClick={() => onPageChange(page + 1)}
          label={getText(BDS_TEXT_TEMPLATE_KEYS.BDS_PAGINATION_NEXT_PAGE, BDS_PAGINATION_NEXT_PAGE_DEFAULT_TEXT)}
          variant="ghost"
          size="large"
          color={color}
          icon={<IconNavArrowRight />}
        />
      </div>
    </div>
  );
};
